import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Upload, ChevronLeft, ChevronRight, FileCheck, X } from "lucide-react";
import {
  isFieldVisible, isFieldRequired, isFieldReadonly, isSectionVisible,
} from "@/lib/formEngine";

function FileField({ field, value, onChange, readonly }) {
  const [busy, setBusy] = useState(false);
  const fileVal = value && typeof value === "object" ? value : null;
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "form_upload");
      const r = await api.post("/uploads", fd);
      onChange({ att_id: r.data.id, name: r.data.original_filename, content_type: r.data.content_type, size: r.data.size });
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`field-${field.name}`}>
      {fileVal ? (
        <div className="flex items-center gap-2 text-sm border border-[#E5E7EB] rounded-lg px-3 py-2 bg-white">
          <FileCheck className="w-4 h-4 text-[#0B7E8C] shrink-0" />
          <a href={`${API}/uploads/${fileVal.att_id}/download?auth=${getToken()}`} target="_blank" rel="noreferrer" className="text-[#0B7E8C] hover:underline truncate">{fileVal.name}</a>
          {!readonly && <button type="button" onClick={() => onChange("")} className="ml-auto text-[#EF4444]" data-testid={`file-clear-${field.name}`}><X className="w-4 h-4" /></button>}
        </div>
      ) : (
        <label className="flex items-center gap-2 border border-dashed border-[#CBD5E1] rounded-lg px-3 py-2.5 text-sm text-[#6B7280] cursor-pointer hover:bg-[#F8FAFC]">
          <Upload className="w-4 h-4" />
          <span>{busy ? "Uploading…" : "Choose a file"}</span>
          <input type="file" className="hidden" disabled={readonly || busy} onChange={pick} />
        </label>
      )}
    </div>
  );
}

function SignaturePad({ value, onChange, readonly, testId }) {
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);
  const last = React.useRef(null);

  const getPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { if (readonly) return; drawing.current = true; last.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.strokeStyle = "#0B1220"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };

  if (readonly) {
    return value
      ? <img src={value} alt="signature" className="border border-[#E5E7EB] rounded-lg bg-white h-24" data-testid={testId} />
      : <div className="text-xs text-[#94A3B8] italic" data-testid={testId}>No signature</div>;
  }
  return (
    <div data-testid={testId}>
      <canvas ref={canvasRef} width={600} height={140}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
        className="border border-[#CBD5E1] rounded-lg bg-white w-full" style={{ height: 140, touchAction: "none" }} />
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-[#94A3B8]">Sign in the box above</span>
        <button type="button" onClick={clear} className="text-xs text-[#0B7E8C] hover:underline" data-testid={`${testId}-clear`}>Clear</button>
      </div>
    </div>
  );
}

function optionsFor(field, lookupOptions) {
  if (field.lookup_category) return lookupOptions[field.lookup_category] || [];
  return (field.options || []).filter((o) => o.active !== false).map((o) => ({ label: o.label, value: o.value }));
}

function FieldControl({ field, value, onChange, readonly, lookupOptions, error }) {
  const opts = optionsFor(field, lookupOptions);
  const common = { "data-testid": `field-${field.name}`, disabled: readonly };
  switch (field.type) {
    case "textarea":
      return <Textarea {...common} value={value || ""} placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
    case "number":
      return <Input {...common} type="number" value={value ?? ""} placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
    case "date":
      return <Input {...common} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "time":
      return <Input {...common} type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "datetime":
      return <Input {...common} type="datetime-local" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "email":
      return <Input {...common} type="email" value={value || ""} placeholder={field.placeholder || "name@example.com"} onChange={(e) => onChange(e.target.value)} />;
    case "phone":
      return <Input {...common} value={value || ""} placeholder={field.placeholder || "+966 5x xxx xxxx"} onChange={(e) => onChange(e.target.value)} />;
    case "address":
      return <Textarea {...common} value={value || ""} placeholder={field.placeholder || "Street, city, region"} onChange={(e) => onChange(e.target.value)} />;
    case "dropdown":
      return (
        <Select value={value || ""} onValueChange={onChange} disabled={readonly}>
          <SelectTrigger data-testid={`field-${field.name}`}><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case "radio":
      return (
        <RadioGroup value={value || ""} onValueChange={onChange} disabled={readonly} className="flex flex-col gap-2 mt-1" data-testid={`field-${field.name}`}>
          {opts.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer">
              <RadioGroupItem value={o.value} /> {o.label}
            </label>
          ))}
        </RadioGroup>
      );
    case "multiselect":
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (val) => {
        if (readonly) return;
        onChange(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
      };
      return (
        <div className="flex flex-col gap-2 mt-1" data-testid={`field-${field.name}`}>
          {opts.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer">
              <Checkbox checked={arr.includes(o.value)} onCheckedChange={() => toggle(o.value)} /> {o.label}
            </label>
          ))}
        </div>
      );
    }
    case "file":
      return <FileField field={field} value={value} onChange={onChange} readonly={readonly} />;
    case "signature":
      return <SignaturePad value={value} onChange={onChange} readonly={readonly} testId={`field-${field.name}`} />;
    case "readonly":
    case "autofill":
    case "calculated":
      return <Input {...common} value={value ?? field.default_value ?? ""} disabled className="bg-[#F8FAFC]" />;
    default:
      return <Input {...common} value={value || ""} placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} maxLength={field.max_length || undefined} />;
  }
}

function FieldRow({ field, value, onChange, role, errors, lookupOptions }) {
  if (field.type === "section_header") {
    return <div className="md:col-span-full pt-2 pb-1 border-b border-[#E5E7EB] text-sm font-semibold text-[#0B7E8C] uppercase tracking-wide">{field.label}</div>;
  }
  const readonly = isFieldReadonly(field, role);
  const required = isFieldRequired(field, {});
  return (
    <div data-testid={`field-row-${field.name}`}>
      <Label className="block text-sm font-medium text-[#1F2937] mb-1.5">
        {field.label}{required && <span className="text-[#EF4444] ml-1">*</span>}
        {readonly && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#94A3B8]">read only</span>}
      </Label>
      <FieldControl field={field} value={value} onChange={onChange} readonly={readonly} lookupOptions={lookupOptions} error={errors[field.name]} />
      {field.help_text && !errors[field.name] && <div className="mt-1 text-xs text-[#6B7280]">{field.help_text}</div>}
      {errors[field.name] && <div className="mt-1 text-xs text-[#EF4444]" data-testid={`field-error-${field.name}`}>{errors[field.name]}</div>}
    </div>
  );
}

const COLS = { single: "grid-cols-1", two: "grid-cols-1 md:grid-cols-2", three: "grid-cols-1 md:grid-cols-3" };

export default function FormRenderer({ definition, role, values, onChange, errors = {} }) {
  const [lookupOptions, setLookupOptions] = useState({});
  const [step, setStep] = useState(0);

  const layout = definition?.layout || "single";
  const sections = useMemo(
    () => (definition?.sections || []).filter((s) => isSectionVisible(s, values, role)).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [definition, values, role],
  );

  useEffect(() => {
    const cats = new Set();
    (definition?.sections || []).forEach((s) => (s.fields || []).forEach((f) => { if (f.lookup_category) cats.add(f.lookup_category); }));
    cats.forEach(async (code) => {
      if (lookupOptions[code]) return;
      try {
        const res = await api.get(`/forms/lookup-options/${code}`);
        setLookupOptions((p) => ({ ...p, [code]: res.data.items }));
      } catch { /* ignore */ }
    });
    // eslint-disable-next-line
  }, [definition]);

  const setField = (name, v) => onChange({ ...values, [name]: v });

  const renderSectionFields = (sec) => {
    const fields = (sec.fields || []).filter((f) => f.active && isFieldVisible(f, values, role)).sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
      <div className={`grid ${COLS[layout] || COLS.single} gap-x-6 gap-y-4`}>
        {fields.map((f) => (
          <FieldRow key={f.id} field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} role={role} errors={errors} lookupOptions={lookupOptions} />
        ))}
        {fields.length === 0 && <div className="text-sm text-[#94A3B8] italic">No visible fields.</div>}
      </div>
    );
  };

  if (sections.length === 0) {
    return <div className="text-sm text-[#94A3B8] py-10 text-center" data-testid="form-empty">This form has no visible sections for the selected role.</div>;
  }

  if (layout === "accordion") {
    return (
      <Accordion type="multiple" defaultValue={sections.map((s) => s.id)} className="space-y-2" data-testid="form-renderer">
        {sections.map((sec) => (
          <AccordionItem key={sec.id} value={sec.id} className="border border-[#E5E7EB] rounded-lg px-4">
            <AccordionTrigger className="text-sm font-semibold text-[#1F2937]">{sec.title}</AccordionTrigger>
            <AccordionContent>
              {sec.description && <p className="text-xs text-[#6B7280] mb-3">{sec.description}</p>}
              {renderSectionFields(sec)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  if (layout === "wizard") {
    const cur = sections[Math.min(step, sections.length - 1)];
    return (
      <div data-testid="form-renderer">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {sections.map((s, i) => (
            <div key={s.id} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${i === step ? "bg-[#0B7E8C] text-white" : "bg-[#F1F5F9] text-[#64748B]"}`}>
              <span className="font-semibold">{i + 1}</span> {s.title}
            </div>
          ))}
        </div>
        <div className="hah-card p-5">
          <h4 className="text-sm font-semibold text-[#1F2937] mb-1">{cur.title}</h4>
          {cur.description && <p className="text-xs text-[#6B7280] mb-3">{cur.description}</p>}
          {renderSectionFields(cur)}
        </div>
        <div className="flex justify-between mt-4">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="wizard-back"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button disabled={step >= sections.length - 1} onClick={() => setStep((s) => s + 1)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="wizard-next">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="form-renderer">
      {sections.map((sec) => (
        <div key={sec.id} className="hah-card p-5">
          <h4 className="text-sm font-semibold text-[#1F2937]">{sec.title}</h4>
          {sec.description && <p className="text-xs text-[#6B7280] mb-3 mt-0.5">{sec.description}</p>}
          <div className="mt-3">{renderSectionFields(sec)}</div>
        </div>
      ))}
    </div>
  );
}
